import apiClient from '../client';
import { APIResponse, MCQPayload, ContentWithMicrotopic } from '@memriti/shared';

export const AssessmentType = {
  QUICK: 'quick',
  COMPREHENSIVE: 'comprehensive'
} as const;

export const Status = {
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed'
} as const;

export type AssessmentType = typeof AssessmentType[keyof typeof AssessmentType];
export type Status = typeof Status[keyof typeof Status];

export interface AssessmentResponse {
  id: number;
  user_id: number;
  start_time: string;
  end_time: string | null;
  status: Status;
  type: AssessmentType;
  questions: MCQQuestion[];
  answers?: {
    content_id: number;
    user_answer: string;
    time_spent: number;
  }[];
  results?: {
    score: number;
    total_questions: number;
    correct_answers: number;
    time_taken: number;
    time_per_question: number[];
    recommendations: string[];
  };
}

export interface MCQQuestion extends ContentWithMicrotopic {
  payload: MCQPayload;
  type: string;
}

export interface AssessmentAnswer {
  questionId: string;
  selectedOptionId: string;
  timeSpent: number;
}

export interface AssessmentResult {
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  timePerQuestion: number[];
  recommendations: string[];
}

interface AnswerResponse {
  isCorrect: boolean;
  explanation: string;
  nextQuestion?: MCQQuestion;
}

export const assessmentService = {
  // Start a new assessment
  startAssessment: async (type: AssessmentType): Promise<AssessmentResponse> => {
    try {
      const response = await apiClient.post<APIResponse<AssessmentResponse>>('/api/v1/assessments', { type });
      console.debug('Assessment started successfully:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('Failed to start assessment:', error);
      throw error;
    }
  },

  // Submit a single answer
  submitAnswer: async (
    assessmentId: number,
    questionId: string,
    selectedOptionId: string,
    timeSpent: number,
    contentType: string
  ): Promise<AnswerResponse> => {
    console.debug('Submitting answer:', { assessmentId, questionId, selectedOptionId, timeSpent, contentType });
    try {
      const response = await apiClient.post<APIResponse<AnswerResponse>>(
        `/api/v1/assessments/${assessmentId}/answers`,
        {
          contentId: questionId,
          userAnswer: selectedOptionId,
          timeSpent,
          contentType
        }
      );
      console.debug('Answer submitted successfully:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('Failed to submit answer:', error);
      throw error;
    }
  },

  // Complete the assessment
  completeAssessment: async (assessmentId: number): Promise<AssessmentResult> => {
    console.debug('Completing assessment:', assessmentId);
    try {
      const response = await apiClient.post<APIResponse<AssessmentResult>>(
        `/api/v1/assessments/${assessmentId}/complete`
      );
      console.debug('Assessment completed successfully:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('Failed to complete assessment:', error);
      throw error;
    }
  },

  // Get latest assessment status
  getLatestAssessment: async (): Promise<AssessmentResponse | null> => {
    console.debug('Fetching latest assessment');
    try {
      const response = await apiClient.get<APIResponse<AssessmentResponse>>('/api/v1/assessments/latest');
      console.debug('Latest assessment retrieved:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('Failed to get latest assessment:', error);
      return null;
    }
  },

  // Flag a question
  flagQuestion: async (
    contentId: number,
    contentType: string,
    reason: string,
    description?: string
  ): Promise<void> => {
    console.debug('Flagging question:', { contentId, contentType, reason, description });
    try {
      const response = await apiClient.post<APIResponse<void>>(
        `/api/v1/questions/${contentId}/flag`,
        {
          reason,
          description: description || '',
          contentType
        }
      );
      console.debug('Question flagged successfully:', response.data);
    } catch (error) {
      console.error('Failed to flag question:', error);
      throw error;
    }
  },

  // Check if question is flagged
  checkQuestionFlag: async (contentId: number, contentType: string): Promise<boolean> => {
    try {
      const response = await apiClient.get<APIResponse<{ id: number } | null>>(
        `/api/v1/questions/${contentId}/flag?contentType=${contentType}`
      );
      // Check if data exists and has an id property (meaning a flag was found)
      // The API returns {} (empty object) when no flag exists, so we check for 'id' property
      const data = response.data.data;
      return data !== null &&
             data !== undefined &&
             typeof data === 'object' &&
             !Array.isArray(data) &&
             'id' in data &&
             Object.keys(data).length > 0; // Ensure it's not an empty object
    } catch (error) {
      console.error('Failed to check question flag:', error);
      return false;
    }
  }
};